import type { NavFolderSlot } from "@/lib/mail/folderNavSections";
import {
  normalizeAccountIconKey,
  type AccountIconKey,
} from "@/features/accounts/accountIconOptions";

/** Entrée de la barre principale — rôle ou dossier IMAP précis. */
export type PrimaryNavEntry =
  | { type: "slot"; slot: NavFolderSlot }
  | { type: "folder"; name: string };

export type { AccountIconKey };

export type MailAccountSettings = {
  /** Icône affichée (localStorage) — auto = logo fournisseur. */
  accountIcon: AccountIconKey;
  /** Cases décochées dans les dossiers principaux. */
  hiddenNavSlots: NavFolderSlot[];
  /** Section « Tous les dossiers » dans la barre latérale. */
  showExtraFolders: boolean;
  /** Signature HTML par compte (localStorage, injectée à la rédaction). */
  signature: string;
  /** Dossier IMAP par rôle (prioritaire sur la détection auto). */
  folderSlotOverrides: Partial<Record<NavFolderSlot, string>>;
  /** Ordre d’affichage dans la barre principale (local uniquement). */
  primaryNavOrder: PrimaryNavEntry[];
  /** Rôles relégués dans « Tous les dossiers ». */
  demotedNavSlots: NavFolderSlot[];
};

const STORAGE_KEY = "mail-account-settings-v1";

export const ACCOUNT_SETTINGS_STORAGE_KEY = STORAGE_KEY;

const DEFAULTS: MailAccountSettings = {
  accountIcon: "provider",
  hiddenNavSlots: [],
  showExtraFolders: true,
  signature: "",
  folderSlotOverrides: {},
  primaryNavOrder: [],
  demotedNavSlots: [],
};

function readAll(): Record<string, MailAccountSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, Partial<MailAccountSettings>>;
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([id, value]) => [id, normalizeSettings(value)])
    );
  } catch {
    return {};
  }
}

function isNavSlot(value: unknown): value is NavFolderSlot {
  return (
    value === "inbox" ||
    value === "outbox" ||
    value === "snoozed" ||
    value === "drafts" ||
    value === "sent" ||
    value === "trash" ||
    value === "junk"
  );
}

function ensureDefaultNavSlots(order: PrimaryNavEntry[]): PrimaryNavEntry[] {
  if (order.length === 0) return order;

  let next = [...order];

  if (!next.some((entry) => entry.type === "slot" && entry.slot === "outbox")) {
    const inboxIdx = next.findIndex((entry) => entry.type === "slot" && entry.slot === "inbox");
    next.splice(inboxIdx >= 0 ? inboxIdx + 1 : 0, 0, { type: "slot", slot: "outbox" });
  }

  if (!next.some((entry) => entry.type === "slot" && entry.slot === "snoozed")) {
    const outboxIdx = next.findIndex((entry) => entry.type === "slot" && entry.slot === "outbox");
    const inboxIdx = next.findIndex((entry) => entry.type === "slot" && entry.slot === "inbox");
    const insertAt = outboxIdx >= 0 ? outboxIdx + 1 : inboxIdx >= 0 ? inboxIdx + 1 : 0;
    next.splice(insertAt, 0, { type: "slot", slot: "snoozed" });
  }

  return next;
}

function normalizePrimaryNavOrder(value: unknown): PrimaryNavEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: PrimaryNavEntry[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const type = (item as PrimaryNavEntry).type;
    if (type === "slot" && isNavSlot((item as { slot?: unknown }).slot)) {
      entries.push({ type: "slot", slot: (item as { slot: NavFolderSlot }).slot });
    } else if (type === "folder" && typeof (item as { name?: unknown }).name === "string") {
      const name = (item as { name: string }).name.trim();
      if (name) entries.push({ type: "folder", name });
    }
  }
  return entries;
}

function normalizeSlotOverrides(
  value: Partial<Record<NavFolderSlot, string>> | undefined
): Partial<Record<NavFolderSlot, string>> {
  if (!value || typeof value !== "object") return {};
  const out: Partial<Record<NavFolderSlot, string>> = {};
  for (const [key, folderName] of Object.entries(value)) {
    if (!isNavSlot(key) || typeof folderName !== "string") continue;
    const trimmed = folderName.trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

function normalizeSettings(value: Partial<MailAccountSettings> | undefined): MailAccountSettings {
  return {
    accountIcon: normalizeAccountIconKey(value?.accountIcon),
    hiddenNavSlots: Array.isArray(value?.hiddenNavSlots)
      ? value.hiddenNavSlots.filter(isNavSlot)
      : [],
    showExtraFolders: value?.showExtraFolders !== false,
    signature: typeof value?.signature === "string" ? value.signature : "",
    folderSlotOverrides: normalizeSlotOverrides(value?.folderSlotOverrides),
    primaryNavOrder: ensureDefaultNavSlots(normalizePrimaryNavOrder(value?.primaryNavOrder)),
    demotedNavSlots: Array.isArray(value?.demotedNavSlots)
      ? value.demotedNavSlots.filter(isNavSlot)
      : [],
  };
}

function writeAll(map: Record<string, MailAccountSettings>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function readAccountSettings(accountId: number): MailAccountSettings {
  return readAll()[String(accountId)] ?? { ...DEFAULTS };
}

export function writeAccountSettings(
  accountId: number,
  patch: Partial<MailAccountSettings>
): MailAccountSettings {
  const map = readAll();
  const key = String(accountId);
  const merged = { ...readAccountSettings(accountId), ...patch };
  const next = normalizeSettings(merged);
  map[key] = next;
  writeAll(map);
  return next;
}
